import ReviewPaneLayout from './ReviewPaneLayout';
import useReviewNoteTarget from './useReviewNoteTarget';
import useVodPlaybackSource from "./useVodPlaybackSource";
import useResourceVisit from '../../hooks/useResourceVisit';
import {playVideoSafely} from "./playVideoSafely";
import {playbackApiBase} from '../../utils/playbackApiBase';
import {SlidersHorizontal} from 'lucide-react';
import ReviewFilterPopover from './ReviewFilterPopover';
// /Volumes/KINGSTON/new-react-project/the-ultimate-guide/src/components/Review/VideoPlayerWithNotes.js

import React, { useState, useEffect, useRef } from 'react';
import FocusPoints from './FocusPoints';
import NoteInputBox from './NoteInputBox';
import NotesDisplay from './NotesDisplay';
import EndOfReviewSummary from './EndOfReviewSummary';
import ScreenshotEditor from './ScreenshotEditor.js';
import SegmentPlaylistVideo from './SegmentPlaylistVideo';
import { db } from '../../Security/firebase-config';
import { getAuth } from 'firebase/auth';
import {
  doc,
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { useTeam } from '../../context/TeamContext';
import {
  refreshVideoProcessingStatus,
  formatProcessingTimeRemaining,
} from '../../utils/videoProcessing';

import VideoControlsBar from './VideoControlsBar';
import { toMatchTimeSec, toVideoTimeSec } from './syncTime';

import './VideoPlayerWithNotes.css';

const timestampToMillis = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
};

const formatPovQualityLabel = (width, height, fps) => {
  const normalizedWidth = Number(width || 0);
  const normalizedHeight = Number(height || 0);
  const normalizedFps = Number(fps || 0);
  const parts = [
    normalizedWidth > 0 && normalizedHeight > 0
      ? `${normalizedWidth}x${normalizedHeight}`
      : null,
    normalizedFps > 0 ? `${Math.round(normalizedFps)}fps` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' | ') : '';
};

const getPovQualityLabel = (pov) => {
  if (!pov || typeof pov !== 'object') return '';

  const processingLabel = formatPovQualityLabel(
    pov.processingTargetWidth,
    pov.processingTargetHeight,
    pov.processingTargetFrameRate
  );
  if (processingLabel) return processingLabel;

  const sourceLabel = formatPovQualityLabel(
    pov.sourceWidth,
    pov.sourceHeight,
    pov.sourceFpsEstimate
  );
  if (sourceLabel) return sourceLabel;

  const capture = pov.capture && typeof pov.capture === 'object' ? pov.capture : null;
  return formatPovQualityLabel(capture?.width, capture?.height, capture?.frameRate);
};

const fallbackProcessingProgress = (stage) => {
  switch (stage) {
    case 'queued':
      return 8;
    case 'starting':
      return 18;
    case 'transcoding':
      return 38;
    case 'finalizing':
      return 92;
    case 'ready':
      return 100;
    case 'failed':
      return 100;
    default:
      return 12;
  }
};

export default function VideoPlayerWithNotes({
  active = true,
  initialNoteInputOpen = true,
  initialReviewTarget = null,
  povSelectionRequest,
  mapId,
  mapName,
  videoId,
  onBackToMaps,
  onBackToVideos,
  onReviewSummary,
  onSwitchPov,
  confirmFocusRemoval = false,
  showFocusOutcomes = true,
  povSwitchRequest, // wrapper tells us when/how to seek after POV swap
}) {
  const suppressNextVideoClickRef = useRef(false);

  const [isBuffering, setIsBuffering] = useState(false);

  /* ──────────────────────────────────────────────────────────────
   * CONSTANTS
   * ────────────────────────────────────────────────────────────── */
  const finishedKey = `review:finished:${mapId}:${videoId}`;
  const SINGLE_CLICK_DELAY = 200; // differentiate single vs double click

  /* ──────────────────────────────────────────────────────────────
   * CONTEXT
   * ────────────────────────────────────────────────────────────── */
  const { teamId, username } = useTeam();

  /* ──────────────────────────────────────────────────────────────
   * REFS (DOM + Imperative flags/timers)
   * ────────────────────────────────────────────────────────────── */
  const videoRef = useRef(null);
  const activeRef = useRef(active);activeRef.current=active;
  useEffect(()=>{if(!active)videoRef.current?.pause();},[active]);
  const videoContainerRef = useRef(null);
  const fsShellRef = useRef(null);
  const filterWrapperRef = useRef(null);
  const notesDisplayRef = useRef(null);

  const isDisplayDraggingRef = useRef(false);   // vertical notes height resizer
  const fsNotesDraggingRef = useRef(false);     // fullscreen notes width resizer
  const singleClickTimerRef = useRef(null);     // distinguish click vs dblclick

  // POV switching: apply seek ONLY after metadata is ready (prevents fighting other effects)
  const pendingSeekRef = useRef(null); // { time: number, play: boolean } | null

  // Session + ping state
  const sessionIdRef = useRef(null);
  const pingTimerRef = useRef(null);
  const playedAccumRef = useRef(0);
  const lastPingTimeRef = useRef(0);
  const lastDurationReportedRef = useRef(null);

  const [matchTitle, setMatchTitle] = useState("");

  const apiBase = playbackApiBase();

  const handlePlaybackBlocked = (message) => {
    setPlaybackBlockedMessage(
      message || 'Your personal egress allowance for this billing cycle has been reached.'
    );
    stopPinging();

    try {
      videoRef.current?.pause?.();
    } catch {}
  };

  // POST helper; sends teamId so metricsStamp can pick it up
  const postJSON = React.useCallback(async (path, body, { beacon = false } = {}) => {
    const q = '';
    const url = `${apiBase}${path}${q}`;
    const payload = JSON.stringify(body);

    if (beacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob); // no headers possible here
      return null;
    }

    const token = await getAuth().currentUser?.getIdToken?.().catch(() => null);
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const res = await fetch(url, { method: 'POST', headers, body: payload }).catch(() => null);
    if (!res) return null;

    try {
      return await res.json();
    } catch {
      return null;
    }
  }, [apiBase]);

  const ensureSessionId = React.useCallback(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      lastDurationReportedRef.current = null;
    }
    return sessionIdRef.current;
  }, []);

  const syncPlaybackDurationToServer = () => {
    if (usesLegacyPublicVideo) return;

    const sid = sessionIdRef.current;
    const d = videoRef.current?.duration;

    if (!sid || !Number.isFinite(d) || d <= 0) return;

    const roundedDuration = Number(d.toFixed(3));
    if (lastDurationReportedRef.current === roundedDuration) return;
    lastDurationReportedRef.current = roundedDuration;

    postJSON('/metrics/view-start', {
      sessionId: sid,
      teamId,
      mapId,
      videoId,
      duration: roundedDuration,
    });
  };

  const startPinging = () => {
    if (usesLegacyPublicVideo) return;
    if (pingTimerRef.current) return;
    lastPingTimeRef.current = Date.now();
    pingTimerRef.current = setInterval(() => {
      const now = Date.now();
      const delta = (now - lastPingTimeRef.current) / 1000; // seconds
      lastPingTimeRef.current = now;
      playedAccumRef.current += delta;

      const sid = ensureSessionId();
      postJSON('/metrics/view-ping', {
        sessionId: sid,
        teamId,
        mapId,
        videoId,
        at: videoRef.current?.currentTime ?? 0,
        playedDelta: Number(delta.toFixed(2)),
        duration: Number.isFinite(videoRef.current?.duration)
          ? Number(videoRef.current.duration.toFixed(3))
          : null,
      }).then((response) => {
        if (response?.allowed === false) {
          handlePlaybackBlocked(response?.message);
        }
      });
    }, 15000); // ~15s
  };

  const stopPinging = () => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  };

  /* ──────────────────────────────────────────────────────────────
   * STATE – top-level flow
   * ────────────────────────────────────────────────────────────── */
  const [finishedReviewing, setFinishedReviewing] = useState(
    () => !initialReviewTarget && sessionStorage.getItem(finishedKey) === 'true'
  );

  /* ──────────────────────────────────────────────────────────────
   * STATE – data
   * ────────────────────────────────────────────────────────────── */
  const [videoData, setVideoData] = useState(null);
  const [loadedVideoIdentity, setLoadedVideoIdentity] = useState('');
  const [focusPoints, setFocusPoints] = useState([]);
  const [allNotes, setAllNotes] = useState([]);
  const [matchId, setMatchId] = useState(null);
  const finishedMatchKey = React.useMemo(() => {
    return matchId ? `review:finished:${mapId}:${matchId}` : null;
  }, [mapId, matchId]);
  const [syncOffsetSec, setSyncOffsetSec] = useState(0);
  const [matchPovs, setMatchPovs] = useState([]); // other POV videos in same match
  const usesLegacyPublicVideo = !videoData?.storagePath && !!videoData?.videoURL;

  const lastAppliedSwitchTokenRef = useRef(null);

  const getPovOptionLabel = React.useCallback((pov) => {
    if (!pov) return 'POV';
    const baseLabel = (
      pov.povName ||
      pov.title ||
      pov.fileName ||
      (typeof pov.id === 'string' ? pov.id.slice(0, 6) : 'POV')
    );
    const qualityLabel = getPovQualityLabel(pov);
    const uploaderLabel =
      typeof pov.createdBy === 'string' && pov.createdBy.trim()
        ? `by ${pov.createdBy.trim()}`
        : '';
    return [baseLabel, qualityLabel || uploaderLabel].filter(Boolean).join(' | ');
  }, []);

  const {src: signedSrc, error: sourceError, loading: sourceLoading, retry: retrySource} = useVodPlaybackSource({
    apiBase, teamId, mapId, videoId,
    videoData: loadedVideoIdentity === `${teamId}/${mapId}/${videoId}` ? videoData : null,
    getSessionId: ensureSessionId,
  });
  const [viewedMedia, setViewedMedia] = useState('');
  const mediaIdentity = JSON.stringify([teamId, mapId, videoId, signedSrc]);
  useResourceVisit({uid:getAuth().currentUser?.uid, kind:'vod', teamId, mapId, resourceId:videoId,
    enabled:viewedMedia === mediaIdentity && loadedVideoIdentity === `${teamId}/${mapId}/${videoId}`});
  const [mediaError, setMediaError] = useState('');
  const automaticRetryRef = useRef({identity: '', count: 0});
  const playVideo = React.useCallback((video = videoRef.current) => !activeRef.current ? Promise.resolve() : playVideoSafely(video, error => {
    setIsBuffering(false);
    setMediaError(error.name === 'NotAllowedError'
      ? 'Your browser blocked playback. Press Play to try again.'
      : 'Could not start this video. Retry playback below.');
  }).then(playing => {
    if(!activeRef.current){video?.pause();return false;}
    if (!playing && video?.paused && !video?.seeking) setIsBuffering(false);
    return playing;
  }), []);
  const [playbackBlockedMessage, setPlaybackBlockedMessage] = useState('');
  const [processingNowMs, setProcessingNowMs] = useState(() => Date.now());

  const processingView = React.useMemo(() => {
    if (!videoData || videoData.status !== 'processing') return null;

    const stage = videoData.processingStage || 'queued';
    const storedProgress = Number(videoData.processingProgressPercent);
    const progressPercent = Number.isFinite(storedProgress)
      ? Math.min(100, Math.max(0, Math.round(storedProgress)))
      : fallbackProcessingProgress(stage);

    const etaAtMs = timestampToMillis(videoData.processingEtaAt);
    const startedAtMs =
      timestampToMillis(videoData.processingStartedAt) ||
      timestampToMillis(videoData.processingQueuedAt);
    const estimatedSeconds = Number(videoData.processingEstimateSeconds || 0);

    let remainingSeconds = null;
    if (etaAtMs) {
      remainingSeconds = Math.max(0, Math.ceil((etaAtMs - processingNowMs) / 1000));
    } else if (startedAtMs && estimatedSeconds > 0) {
      remainingSeconds = Math.max(
        0,
        Math.ceil(estimatedSeconds - ((processingNowMs - startedAtMs) / 1000))
      );
    }

    return {
      progressPercent,
      message:
        videoData.processingMessage ||
        'This upload is being optimized to a maximum of 1080p and 60fps before playback becomes available.',
      etaLabel: formatProcessingTimeRemaining(remainingSeconds),
      explanation:
        videoData.processingMode === 'oversized_transcode'
          ? 'This upload exceeded the platform playback target, so it is being optimized before teammates can watch it.'
          : 'This video is still being prepared for playback.',
    };
  }, [processingNowMs, videoData]);

  const isProcessingVideo = !!processingView;
  const isFailedVideo = videoData?.status === 'failed';
  const canAddNotes = videoData?.status !== 'processing' && videoData?.status !== 'failed';
  const canRenderVideo =
    Boolean(signedSrc) &&
    !playbackBlockedMessage &&
    !sourceError &&
    !isProcessingVideo &&
    !isFailedVideo;

  /* ──────────────────────────────────────────────────────────────
  * STATE – layout / fullscreen
  * ────────────────────────────────────────────────────────────── */
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFsNotes, setShowFsNotes] = useState(false);

  const getRootFontPx = () => {
    try {
      return (
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      );
    } catch {
      return 16;
    }
  };

  const [fsNotesWidthRem, setFsNotesWidthRem] = useState(() => {
    const isSmall = window.matchMedia?.("(max-width: 40rem)")?.matches;
    return isSmall ? 16 : 24;
  });

  const [isSoftFullscreen, setIsSoftFullscreen] = useState(false);

  /* ──────────────────────────────────────────────────────────────
   * POV SWITCH: store a pending seek that will be applied on metadata/canplay
   * ────────────────────────────────────────────────────────────── */
  // keep track of what token is pending (not applied)
  const pendingTokenRef = useRef(null);
  useReviewNoteTarget({active,target:initialReviewTarget,teamId,mapId,videoId,src:signedSrc,videoData,loadedVideoIdentity,videoRef,pendingSeekRef,onOpen:setFinishedReviewing});

  useEffect(() => {
    if (!povSwitchRequest) return;

    // Only apply when THIS player instance is the requested POV
    if (povSwitchRequest.nextVideoId !== videoId) return;

    // Restore UI state immediately
    setShowFsNotes(!!povSwitchRequest.keepNotesOpen);
    setShowInput(
      typeof povSwitchRequest.keepNoteInputOpen === 'boolean'
        ? povSwitchRequest.keepNoteInputOpen
        : true
    );

    // Wait until we have src AND videoData loaded (so offset is correct)
    if (!signedSrc) return;
    if (!videoData || loadedVideoIdentity !== `${teamId}/${mapId}/${videoId}`) return;

    const offset = Number.isFinite(Number(videoData.syncOffsetSec))
      ? Number(videoData.syncOffsetSec)
      : 0;

    const targetVideoTime = toVideoTimeSec(povSwitchRequest.matchTimeSec, offset);
    const safeTime = Math.max(0, targetVideoTime);

    pendingSeekRef.current = { time: safeTime, play: !!povSwitchRequest.wasPlaying };
    pendingTokenRef.current = povSwitchRequest.token;
  }, [povSwitchRequest, videoId, signedSrc, videoData, loadedVideoIdentity, teamId, mapId]);

  useEffect(() => {
    if (!povSwitchRequest) return;
    if (povSwitchRequest.nextVideoId !== videoId) return;
    if (!povSwitchRequest.wasFullscreen) return;
    if (!signedSrc || !videoData) return;

    const shell = fsShellRef.current;
    if (!shell) return;

    const shellIsNativeFs = document.fullscreenElement === shell;

    // already restored
    if (shellIsNativeFs || isSoftFullscreen) return;

    const restoreFullscreen = async () => {
      try {
        if (povSwitchRequest.wasSoftFullscreen) {
          setIsSoftFullscreen(true);
          return;
        }

        if (document.fullscreenEnabled && shell.requestFullscreen) {
          await shell.requestFullscreen({ navigationUI: 'hide' });
        } else {
          setIsSoftFullscreen(true);
        }
      } catch (err) {
        setIsSoftFullscreen(true);
      }
    };

    restoreFullscreen();
  }, [povSwitchRequest, videoId, signedSrc, videoData, isSoftFullscreen]);

  const applyPendingSeek = React.useCallback(() => {
    const v = videoRef.current;
    const pending = pendingSeekRef.current;
    if (!v || !pending) return;
  
    try {
      if (Math.abs(v.currentTime - pending.time) > 0.001) v.currentTime = pending.time;
    } catch {}
  
    if (pending.play) void playVideo(v);
  
    pendingSeekRef.current = null;
  
    // ✅ NOW we can say "this token is applied"
    if (pendingTokenRef.current != null) {
      lastAppliedSwitchTokenRef.current = pendingTokenRef.current;
      pendingTokenRef.current = null;
    }
  }, [playVideo]);
  
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!pendingSeekRef.current) return;
  
    // If metadata is already loaded, apply immediately
    if (v.readyState >= 1) applyPendingSeek();
  }, [signedSrc, videoId, videoData, povSwitchRequest, applyPendingSeek]);  

  /* ──────────────────────────────────────────────────────────────
   * STATE – playback
   * ────────────────────────────────────────────────────────────── */
  const [currentTime, setCurrentTime] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  /* ──────────────────────────────────────────────────────────────
   * STATE – screenshot editor
   * ────────────────────────────────────────────────────────────── */
  const [screenshotEditorOpen, setScreenshotEditorOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  /* ──────────────────────────────────────────────────────────────
   * STATE – UI modes / filters
   * ────────────────────────────────────────────────────────────── */
  const [autoScroll, setAutoScroll] = useState(true);
  const [showInput, setShowInput] = useState(initialNoteInputOpen);
  const [displayMode, setDisplayMode] = useState('list'); // 'list' | 'scroller'

  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterMentions, setFilterMentions] = useState([]); // strings sans '@'
  const [filterImportance, setFilterImportance] = useState([]); // [1,2,3]
  const [filterTags, setFilterTags] = useState([]); // strings


  useEffect(() => {
    setPlaybackBlockedMessage('');
  }, [teamId, videoId]);
  const [hideReviewedByMe, setHideReviewedByMe] = useState(false);

  /* ──────────────────────────────────────────────────────────────
   * STATE – layout / fullscreen
   * ────────────────────────────────────────────────────────────── */
  const [notesDisplayHeight, setNotesDisplayHeight] = useState(350);

  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 1108);

  const isCoarsePointer = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  };

  const isIPhone = () => {
    if (typeof navigator === 'undefined') return false;
    return /\biPhone\b/i.test(navigator.userAgent);
  };

  /* ──────────────────────────────────────────────────────────────
  * STATE – controls auto-hide + soft fullscreen landscape forcing
  * ────────────────────────────────────────────────────────────── */
  const controlsHideTimerRef = useRef(null);
  const [controlsVisible, setControlsVisible] = useState(true);

  const markControlsActive = React.useCallback(() => {
    // Always show immediately on activity
    setControlsVisible(true);

    // If paused, keep controls visible (no auto-hide)
    if (!isPlaying) {
      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
      controlsHideTimerRef.current = null;
      return;
    }

    // Restart auto-hide timer
    if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    // When play/pause toggles, reset visibility appropriately
    if (!isPlaying) {
      setControlsVisible(true);
      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
      controlsHideTimerRef.current = null;
      return;
    }

    // If we started playing, begin the hide timer
    markControlsActive();

    return () => {
      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
      controlsHideTimerRef.current = null;
    };
  }, [isPlaying, markControlsActive]);

  const [forceLandscapeSoftFs, setForceLandscapeSoftFs] = useState(false);

  useEffect(() => {
    if (!isSoftFullscreen) {
      setForceLandscapeSoftFs(false);
      return;
    }

    const update = () => {
      const portrait =
        window.matchMedia?.('(orientation: portrait)')?.matches ??
        (window.innerHeight > window.innerWidth);

      // Only force-rotate in soft fullscreen on coarse pointers (phones/tablets)
      const shouldForce = portrait && isCoarsePointer();
      setForceLandscapeSoftFs(shouldForce);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [isSoftFullscreen]);

  const syncDuration = () => {
    const d = videoRef.current?.duration;
    if (Number.isFinite(d) && d > 0) {
      setDurationSec(d);
      syncPlaybackDurationToServer();
    }
  };

  const retryPlayback = React.useCallback(() => {
    const video = videoRef.current;
    if (video) pendingSeekRef.current = {time: Math.max(0, video.currentTime || 0), play: !video.paused};
    setMediaError('');
    setPlaybackBlockedMessage('');
    setIsBuffering(false);
    if (usesLegacyPublicVideo && video) video.load();
    retrySource();
  }, [retrySource, usesLegacyPublicVideo]);

  useEffect(() => {
    setIsBuffering(false);
    setMediaError('');
    setIsPlaying(false);
    if (pingTimerRef.current) {clearInterval(pingTimerRef.current); pingTimerRef.current = null;}
  }, [signedSrc, mapId, videoId]);

  useEffect(() => {
    if (!isBuffering) return undefined;
    const timer = window.setTimeout(() => {
      setIsBuffering(false);
      setMediaError('Playback is taking longer than expected. Check your connection or retry playback.');
    }, 30000);
    return () => window.clearTimeout(timer);
  }, [isBuffering]);

  const handleMediaError = () => {
    setIsBuffering(false);
    setIsPlaying(false);
    stopPinging();
    const identity = `${teamId}/${mapId}/${videoId}`;
    if (automaticRetryRef.current.identity !== identity) automaticRetryRef.current = {identity, count: 0};
    const code = videoRef.current?.error?.code;
    if ((code === 2 || code === 4) && automaticRetryRef.current.count < 1) {
      automaticRetryRef.current.count++;
      retryPlayback();
      return;
    }
    setMediaError(code === 3 || code === 4
      ? 'This recording could not be loaded or decoded. Retry playback; if it continues, the video may need to be prepared again.'
      : 'The video could not finish loading. Check your connection and retry playback.');
  };

  useEffect(() => {
    if (!teamId || !mapId || !videoId || videoData?.status !== 'processing') return undefined;

    let cancelled = false;
    let requestInFlight = false;

    const refresh = async () => {
      if (requestInFlight || cancelled) return;
      requestInFlight = true;
      try {
        await refreshVideoProcessingStatus({teamId, mapId, videoId});
      } catch (error) {
        console.warn('[VideoPlayerWithNotes] processing refresh failed:', error);
      } finally {
        requestInFlight = false;
      }
    };

    refresh();
    const refreshInterval = window.setInterval(refresh, 15000);
    const timerInterval = window.setInterval(() => {
      if (!cancelled) setProcessingNowMs(Date.now());
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(refreshInterval);
      window.clearInterval(timerInterval);
    };
  }, [mapId, teamId, videoData?.status, videoId]);

  /* ──────────────────────────────────────────────────────────────
   * CDN
   * ────────────────────────────────────────────────────────────── */
  // Works in CRA (REACT_APP_*) and also in Vite (VITE_*)
  /* ──────────────────────────────────────────────────────────────
   * MEMO – derived filter options
   * ────────────────────────────────────────────────────────────── */
  const mentionOptions = React.useMemo(() => {
    const allAt = allNotes.flatMap((n) => n.at || []);
    const map = new Map(); // lower → original (without '@')
    allAt.forEach((raw) => {
      const stripped = raw.replace(/^@/, '');
      const lower = stripped.toLowerCase();
      if (!map.has(lower)) map.set(lower, stripped);
    });
    return Array.from(map.entries())
      .filter(([lower]) => lower !== 'team')
      .map(([, orig]) => '@' + orig);
  }, [allNotes]);

  const tagOptions = React.useMemo(() => {
    const allTags = allNotes.flatMap((n) => n.tags || []);
    const uniq = Array.from(new Set(allTags.map((t) => t.toLowerCase())));
    return uniq;
  }, [allNotes]);

  const includeTeam = filterMentions.length > 0;
  const filters = {
    filterMentions,
    includeTeam,
    filterImportance,
    filterTags,
    hideReviewedByMe,
    currentUser: username,
  };

  const currentMatchTimeSec = React.useMemo(() => {
    return Math.max(0, toMatchTimeSec(currentTime, syncOffsetSec));
  }, [currentTime, syncOffsetSec]);

  /* ──────────────────────────────────────────────────────────────
   * EFFECTS – persist finished flag
   * ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    sessionStorage.setItem(finishedKey, finishedReviewing ? 'true' : 'false');
    if (finishedMatchKey) {
      sessionStorage.setItem(finishedMatchKey, finishedReviewing ? 'true' : 'false');
    }
  }, [finishedReviewing, finishedKey, finishedMatchKey]);

  /* ──────────────────────────────────────────────────────────────
   * EFFECTS – global mouse listeners (dragging/resizing)
   * ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    // Fullscreen notes width drag
    const onMouseMoveFsNotes = (e) => {
      if (!fsNotesDraggingRef.current || !fsShellRef.current) return;
      const shellRect = fsShellRef.current.getBoundingClientRect();
      const rootPx = getRootFontPx();

      let newWidthPx = shellRect.right - e.clientX; // px
      let newWidthRem = newWidthPx / rootPx;

      // Keep notes usable + video visible (all in rem)
      const MIN_NOTES_REM = 14;  // ~224px
      const MIN_VIDEO_REM = 18;  // ~288px

      const maxNotesRem = Math.max(
        MIN_NOTES_REM,
        (shellRect.width / rootPx) - MIN_VIDEO_REM
      );

      if (newWidthRem < MIN_NOTES_REM) newWidthRem = MIN_NOTES_REM;
      if (newWidthRem > maxNotesRem) newWidthRem = maxNotesRem;

      setFsNotesWidthRem(newWidthRem);
    };
    const onMouseUpFsNotes = () => {
      if (fsNotesDraggingRef.current) {
        fsNotesDraggingRef.current = false;
        document.body.style.userSelect = '';
      }
    };

    // Notes vertical height
    const onMouseMoveDisplay = (e) => {
      if (!isDisplayDraggingRef.current || !notesDisplayRef.current) return;
      const containerTop = notesDisplayRef.current.getBoundingClientRect().top;
      let newH = e.clientY - containerTop;
      const MIN_H = 100;
      const parentHeight = notesDisplayRef.current.parentNode.getBoundingClientRect().height;
      const MAX_H = parentHeight - 100;
      if (newH < MIN_H) newH = MIN_H;
      if (newH > MAX_H) newH = MAX_H;
      setNotesDisplayHeight(newH);
    };
    const onMouseUpDisplay = () => {
      if (isDisplayDraggingRef.current) {
        isDisplayDraggingRef.current = false;
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', onMouseMoveFsNotes);
    document.addEventListener('mouseup', onMouseUpFsNotes);


    document.addEventListener('mousemove', onMouseMoveDisplay);
    document.addEventListener('mouseup', onMouseUpDisplay);

    return () => {
      document.removeEventListener('mousemove', onMouseMoveFsNotes);
      document.removeEventListener('mouseup', onMouseUpFsNotes);

    
      document.removeEventListener('mousemove', onMouseMoveDisplay);
      document.removeEventListener('mouseup', onMouseUpDisplay);
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────
   * EFFECTS – click-outside to close filter panel
   * ────────────────────────────────────────────────────────────── */


  /* ──────────────────────────────────────────────────────────────
   * EFFECTS – fullscreen state tracking
   * ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const onFsChange = () => {
      const shell = fsShellRef.current;

      // Prevent null === null from incorrectly counting as fullscreen
      const shellIsNativeFs = !!shell && document.fullscreenElement === shell;
      const inAnyFs = shellIsNativeFs || isSoftFullscreen;

      setIsFullscreen(inAnyFs);

      // If we fully exited ALL fullscreen modes, close FS notes
      if (!shellIsNativeFs && !isSoftFullscreen) {
        setShowFsNotes(false);
      }
    };

    document.addEventListener('fullscreenchange', onFsChange);

    // wait one frame so fsShellRef.current is attached before first check
    requestAnimationFrame(onFsChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, [isSoftFullscreen]);

  useEffect(() => {
    if (!isSoftFullscreen) return;

    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    // ✅ Try to lock orientation to landscape while in soft fullscreen.
    // Many mobile browsers ignore this unless user-gesture / PWA, but it's safe.
    window.screen?.orientation?.lock?.('landscape').catch(() => {});

    return () => {
      // ✅ Unlock when leaving soft fullscreen
      window.screen?.orientation?.unlock?.();

      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [isSoftFullscreen]);

  /* ──────────────────────────────────────────────────────────────
   * EFFECTS – cleanup pending single click timers
   * ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    return () => {
      if (singleClickTimerRef.current) {
        clearTimeout(singleClickTimerRef.current);
      }
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────
   * EFFECTS – fetch video + focus points + subscribe to notes
   * ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!teamId || !mapId || !videoId) {
      setVideoData(null);
      setMatchId(null);
      setMatchPovs([]);
      setAllNotes([]);
      setFocusPoints([]);
      setMatchTitle("");
      return;
    }

    setVideoData(null);
    setMatchId(null);
    setMatchPovs([]);
    setAllNotes([]);
    setFocusPoints([]);
    setMatchTitle("");

    const videoDocRef = doc(db, 'teams', teamId, 'maps', mapId, 'videos', videoId);
    const unsubVideo = onSnapshot(
      videoDocRef,
      (snap) => {
        if (!snap.exists()) {
          setVideoData(null);
          setMatchId(null);
          return;
        }
        const data = snap.data();
        setLoadedVideoIdentity(`${teamId}/${mapId}/${videoId}`);
        setVideoData(data);

        // attach match + sync
        setMatchId(data.matchId || null);
        setSyncOffsetSec(
          Number.isFinite(Number(data.syncOffsetSec)) ? Number(data.syncOffsetSec) : 0
        );
      },
      (err) => {
        console.error('[VideoPlayerWithNotes] video onSnapshot error:', err);
        setVideoData(null);
        setMatchId(null);
      }
    );

    return () => {
      unsubVideo();
    };
  }, [teamId, mapId, videoId]);

  useEffect(() => {
    if (!teamId || !mapId || !matchId) {
      setMatchPovs([]);
      return;
    }

    // Query all videos under this map that belong to this match
    const vidsRef = collection(db, 'teams', teamId, 'maps', mapId, 'videos');
    const qRef = query(vidsRef, where('matchId', '==', matchId));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const povs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Optional: stable ordering
        povs.sort((a, b) => getPovOptionLabel(a).localeCompare(getPovOptionLabel(b)));

        setMatchPovs(povs);
      },
      (err) => console.error('[VideoPlayerWithNotes] match POV query error:', err)
    );

    return () => unsub();
  }, [teamId, mapId, matchId, getPovOptionLabel]);

  useEffect(() => {
    if (!teamId || !mapId || !matchId) {
      setAllNotes([]);
      return;
    }

    const notesColRef = collection(db, 'teams', teamId, 'maps', mapId, 'matches', matchId, 'notes');

    const unsubscribe = onSnapshot(
      notesColRef,
      (snapshot) => {
        const notes = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllNotes(notes);
      },
      (err) => {
        console.error('[VideoPlayerWithNotes] match notes onSnapshot error:', err);
        setAllNotes([]);
      }
    );

    return () => unsubscribe();
  }, [teamId, mapId, matchId]);

  useEffect(() => {
    if (!teamId || !mapId || !videoId) {
      setFocusPoints([]);
      return;
    }

    console.log("[FP] teamId:", teamId, "mapId:", mapId, "videoId:", videoId, "matchId:", matchId);
    const pointsRef = matchId
      ? collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "focusPoints")
      : collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "focusPoints");

    const qRef = query(pointsRef, orderBy("order", "asc"));
    console.log("[FP] pointsRef path:", pointsRef.path);

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        setFocusPoints(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        console.log("[FP] docs:", snap.size, snap.docs.map(d => d.id));
      },
      (err) => {
        console.error("[VideoPlayerWithNotes] focus points onSnapshot error:", err);
        setFocusPoints([]);
      }
    );

    return () => unsub();
  }, [teamId, mapId, videoId, matchId]);

  const requestFullscreenNow = React.useCallback(() => {
    const shell = fsShellRef.current;
    if (!shell) return;

    const canStdFS = !!(document.fullscreenEnabled && shell.requestFullscreen);

    // ✅ Choose when to FORCE soft fullscreen
    // - iPhone: always soft (native is painful + blocks custom UI)
    // - coarse pointer: typically mobile/tablet; you can choose to force soft here too
    const forceSoft = isIPhone() || isCoarsePointer();

    try {
      // EXIT path
      if (isSoftFullscreen) {
        setIsSoftFullscreen(false);
        return;
      }
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
        return;
      }

      // ENTER path
      if (!forceSoft && canStdFS) {
        const p = shell.requestFullscreen({ navigationUI: 'hide' });
        p?.catch(() => {
          // if denied, fall back to soft
          setIsSoftFullscreen(true);
        });
        return;
      }

      // ✅ Soft fullscreen fallback (and forced mode)
      setIsSoftFullscreen(true);
    } catch (err) {
      console.error('Fullscreen error:', err);
      setIsSoftFullscreen(true);
    }
  }, [isSoftFullscreen]);

  /* ──────────────────────────────────────────────────────────────
   * Keyboard shortcuts
   * ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const isTyping = (el) => {
      const tag = el?.tagName?.toUpperCase();
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        el?.isContentEditable ||
        (tag === 'INPUT' && el.type === 'range')
      );
    };

    const onKeyDown = (e) => {
      if(!activeRef.current)return;
      if (isTyping(document.activeElement) || e.target?.closest?.('[role=separator]')) return;

      const k = (e.key || '').toLowerCase();
      const code = e.code || '';

      const consume = () => {
        e.preventDefault?.();
        e.stopPropagation?.();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      };

      const video = videoRef.current;
      if (!video) return;

      if (k === ' ' || k === 'k' || code === 'Space') {
        consume();
        video.paused ? void playVideo(video) : video.pause();
        return;
      }
      if (k === 'm') {
        consume();
        video.muted = !video.muted;
        return;
      }
      if (k === 'f') {
        consume();
        requestFullscreenNow();
        return;
      }
      if (k === 'arrowright' || k === 'l') {
        consume();
        video.currentTime = Math.min(video.duration ?? Infinity, (video.currentTime || 0) + 10);
        return;
      }
      if (k === 'arrowleft' || k === 'j') {
        consume();
        video.currentTime = Math.max(0, (video.currentTime || 0) - 10);
        return;
      }
      if (k === 'arrowup') {
        consume();
        video.muted = false;
        video.volume = Math.min(1, (video.volume ?? 1) + 0.05);
        return;
      }
      if (k === 'arrowdown') {
        consume();
        video.volume = Math.max(0, (video.volume ?? 1) - 0.05);
        return;
      }
      if (k === 'escape' && document.fullscreenElement) {
        consume();
        document.exitFullscreen?.();
        return;
      }
    };

    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [requestFullscreenNow, playVideo]);

  /* ──────────────────────────────────────────────────────────────
   * Metrics flush on leave
   * ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const flushAndStop = () => {
      if (!sessionIdRef.current) return;
      if (usesLegacyPublicVideo) return;
      stopPinging();
      postJSON(
        '/metrics/view-complete',
        {
          sessionId: sessionIdRef.current,
          teamId,
          mapId,
          videoId,
          totalPlayed: Number(playedAccumRef.current.toFixed(2)),
          ended: false,
        },
        { beacon: true }
      );
    };

    const onHidden = () => {
      if (document.visibilityState === 'hidden') flushAndStop();
    };

    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('beforeunload', flushAndStop);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('beforeunload', flushAndStop);
    };
  }, [mapId, postJSON, teamId, usesLegacyPublicVideo, videoId]);

  useEffect(() => {
    return () => {
      stopPinging();
    };
  }, []);

  useEffect(() => {
    if (!teamId || !mapId || !matchId) {
      setMatchTitle("");
      return;
    }

    const ref = doc(db, "teams", teamId, "maps", mapId, "matches", matchId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setMatchTitle(snap.exists() ? (snap.data()?.title || "") : "");
      },
      (error) => {
        console.error("[VideoPlayerWithNotes] match title onSnapshot error:", error);
        setMatchTitle("");
      }
    );

    return () => unsub();
  }, [teamId, mapId, matchId]);

  useEffect(() => {
    const onResize = () => {
      const narrow = window.innerWidth <= 1108;
      setIsNarrow(narrow);


    };

    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // wherever you have access to the <video> ref
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const log = (tag) => {
      const seekable =
        v.seekable && v.seekable.length
          ? `${v.seekable.start(0)}-${v.seekable.end(0)}`
          : "none";

      console.log(
        `[video] ${tag} | duration=${v.duration} | currentTime=${v.currentTime} | seekable=${seekable} | readyState=${v.readyState}`
      );
    };

    const onLoadedMetadata = () => log("loadedmetadata");
    const onDurationChange = () => log("durationchange");
    const onCanPlay = () => log("canplay");
    const onProgress = () => log("progress"); // helpful for seekable range

    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("durationchange", onDurationChange);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("progress", onProgress);

    return () => {
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("durationchange", onDurationChange);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("progress", onProgress);
    };
  }, [signedSrc]);

  /* ──────────────────────────────────────────────────────────────
   * HANDLERS – playback/time
   * ────────────────────────────────────────────────────────────── */
  const handleTimeUpdate = () => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  };

  const handlePlay = () => {
    if (playbackBlockedMessage) {
      handlePlaybackBlocked(playbackBlockedMessage);
      return;
    }

    setIsPlaying(true);
    if (usesLegacyPublicVideo) return;

    const sid = ensureSessionId();

    if (playedAccumRef.current === 0) {
      const d = videoRef.current?.duration;
      postJSON('/metrics/view-start', {
        sessionId: sid,
        teamId,
        mapId,
        videoId,
        duration: Number.isFinite(d) && d > 0 ? d : null,
      });
    }
    startPinging();
  };

  const handlePause = () => {
    if (!videoRef.current?.seeking) setIsBuffering(false);
    setIsPlaying(false);
    stopPinging();
  };

  const handleEnded = () => {
    setIsPlaying(false);
    stopPinging();
    if (!usesLegacyPublicVideo) {
      postJSON('/metrics/view-complete', {
        sessionId: sessionIdRef.current,
        teamId,
        mapId,
        videoId,
        totalPlayed: Number(playedAccumRef.current.toFixed(2)),
        ended: true,
      });
    }
    playedAccumRef.current = 0;
    sessionIdRef.current = null;
    lastDurationReportedRef.current = null;
  };

  /* ──────────────────────────────────────────────────────────────
   * HANDLERS – resizing starts
   * ────────────────────────────────────────────────────────────── */
  const startDisplayDrag = () => {
    isDisplayDraggingRef.current = true;
    document.body.style.userSelect = 'none';
  };
  const startFsNotesDrag = (e) => {
    fsNotesDraggingRef.current = true;
    document.body.style.userSelect = 'none';
    e.preventDefault();
  };

  /* ──────────────────────────────────────────────────────────────
   * HANDLERS – screenshot capture
   * ────────────────────────────────────────────────────────────── */
  const handleCaptureScreenshot = async () => {
    if (!videoRef.current) return;

    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (e) {
        console.warn('Could not exit fullscreen before screenshot:', e);
      }
    }

    const video = videoRef.current;
    video.pause();

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataURL = canvas.toDataURL('image/png');
    setCapturedImage(dataURL);
    setScreenshotEditorOpen(true);
  };

  /* ──────────────────────────────────────────────────────────────
   * HANDLERS – display mode / autoscroll
   * ────────────────────────────────────────────────────────────── */
  const onModeButtonClick = (mode) => {
    if (mode === 'scroller') {
      setDisplayMode('scroller');
      setAutoScroll(true);
    } else {
      setDisplayMode('list');
    }
  };
  const handleDisableAutoScroll = () => setAutoScroll(false);

  /* ──────────────────────────────────────────────────────────────
   * HANDLERS – fullscreen toggles
   * ────────────────────────────────────────────────────────────── */
  const toggleFullscreenNotes = () => {
    setShowFsNotes((v) => !v);
  };

  /* ──────────────────────────────────────────────────────────────
   * HANDLERS – Pov switcher
   * ────────────────────────────────────────────────────────────── */
  const selectionHandler = useRef(null),handledSelection = useRef(null);
  useEffect(()=>{
    if(!active||!povSelectionRequest||handledSelection.current===povSelectionRequest.token)return;
    handledSelection.current=povSelectionRequest.token;selectionHandler.current?.(povSelectionRequest.nextVideoId);
  },[active,povSelectionRequest]);
  const handleSwitchPovClick = async (nextVideoId) => {
    if (!nextVideoId || nextVideoId === videoId) return;

    // prevent the POV button click from also toggling play/pause
    suppressNextVideoClickRef.current = true;

    const v = videoRef.current;
    const wasPlaying = v ? !v.paused : false;
    const mt = v ? toMatchTimeSec(v.currentTime, syncOffsetSec) : currentMatchTimeSec;

    const shell = fsShellRef.current;
    const wasNativeFullscreen = !!shell && document.fullscreenElement === shell;
    const wasFullscreen = wasNativeFullscreen || isSoftFullscreen;

    onSwitchPov?.({
      nextVideoId,
      matchTimeSec: mt,
      wasPlaying,
      wasFullscreen,
      wasSoftFullscreen: isSoftFullscreen,
      keepNotesOpen: showFsNotes,
      keepNoteInputOpen: showInput,
    });
  };

  selectionHandler.current=handleSwitchPovClick;

  /* ──────────────────────────────────────────────────────────────
  * HANDLERS – finish reviewing (exit fullscreen first)
  * ────────────────────────────────────────────────────────────── */
  const exitAnyFullscreen = React.useCallback(async () => {
    // Close overlays first (prevents weird positioning during teardown)
    setShowFilterPanel(false);
    setShowFsNotes(false);

    // Soft fullscreen (your mobile mode)
    if (isSoftFullscreen) {
      setIsSoftFullscreen(false);
    }

    // Native fullscreen
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {}
    }
  }, [isSoftFullscreen]);

  const handleFinishedReviewing = React.useCallback(async () => {
    await exitAnyFullscreen();

    // Let layout settle one frame before swapping to summary
    requestAnimationFrame(() => {
      if (onReviewSummary) onReviewSummary();
      else setFinishedReviewing(true);
    });
  }, [exitAnyFullscreen, onReviewSummary]);

  /* ──────────────────────────────────────────────────────────────
  * EARLY RETURN – Finished review screen
  * ────────────────────────────────────────────────────────────── */
  if (finishedReviewing && !onReviewSummary) {
    return (
      <EndOfReviewSummary
        showFocusOutcomes={showFocusOutcomes}
        mapId={mapId}
        videoId={videoId}
        mapName={mapName}
        videoTitle={videoData?.title}
        onBackToMaps={() => {
          setFinishedReviewing(false);
          onBackToMaps();
        }}
        onBackToVideos={() => {
          setFinishedReviewing(false);
          onBackToVideos();
        }}
        onResumeReview={() => setFinishedReviewing(false)}
      />
    );
  }

  if (videoData?.playbackMode === 'segment_playlist' && !isFailedVideo) {
    return (
      <div className="video-review-container">
        <div className="vpwn-breadcrumb">
          <span className="vpwn-breadcrumb-link" onClick={onBackToMaps}>
            Map Selection
          </span>
          <span className="vpwn-breadcrumb-sep">/</span>

          <span className="vpwn-breadcrumb-link" onClick={onBackToVideos}>
            {mapName}
          </span>
          <span className="vpwn-breadcrumb-sep">/</span>

          <span className="vpwn-breadcrumb-current">
            {(matchTitle || `Match ${matchId?.slice(0, 8)}`) +
              (videoData?.povName ? ` • ${videoData.povName}` : "")}
          </span>
        </div>

        

        <FocusPoints
              confirmRemoval={confirmFocusRemoval}
              showOutcomes={showFocusOutcomes}
          mapId={mapId}
          videoId={videoId}
          matchId={matchId}
          focusPoints={focusPoints}
          teamId={teamId}
        />

        <div style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(22rem, 0.9fr)'}}>
          <div
            style={{
              background: 'var(--Rev-VidPlayNotes-bg-clr, rgba(15, 24, 36, 0.92))',
              borderRadius: '1rem',
              padding: '1rem',
              boxShadow: '0 0 1rem rgba(0, 0, 0, 0.25)',
            }}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '0.75rem'}}>
              <div>
                <h3 style={{margin: 0}}>{videoData?.title || 'Live Lobby Recording'}</h3>
                <p style={{margin: '0.35rem 0 0', opacity: 0.82}}>
                  Near-live segmented archive{videoData?.liveLobbyEndedReason ? ` • Ended: ${videoData.liveLobbyEndedReason}` : ''}
                </p>
              </div>

              <button
                className="Rev-VidPlayNotes-mode-btn Finished-reviewing-btn"
                onClick={handleFinishedReviewing}
              >
                Review Done
              </button>
            </div>

            <SegmentPlaylistVideo
              teamId={teamId}
              mapId={mapId}
              videoId={videoId}
              lobbyId={videoData?.liveLobbyId}
              liveMode={false}
              lobbyStatus={videoData?.status}
              onCurrentTimeChange={setCurrentTime}
              onPlaybackBlocked={handlePlaybackBlocked}
              onPlaybackReady={() => setViewedMedia(mediaIdentity)}
            />
          </div>

          <div
            style={{
              background: 'var(--Rev-VidPlayNotes-bg-clr, rgba(15, 24, 36, 0.92))',
              borderRadius: '1rem',
              padding: '1rem',
              boxShadow: '0 0 1rem rgba(0, 0, 0, 0.25)',
              minHeight: '20rem',
            }}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem'}}>
              <div>
                <h4 style={{margin: 0}}>Notes</h4>
                <p style={{margin: '0.35rem 0 0', opacity: 0.82}}>
                  Segmented live-lobby recordings keep the same shared note timeline.
                </p>
              </div>

              {showInput ? (
                <button className="hide-btn" onClick={() => setShowInput(false)}>
                  Hide Notes
                </button>
              ) : (
                <button className="show-input-btn" onClick={() => setShowInput(true)}>
                  + Add Note
                </button>
              )}
            </div>

            {showInput && canAddNotes && (
              <NoteInputBox
                mapId={mapId}
                matchId={matchId}
                matchTimeSec={currentMatchTimeSec}
                onHide={() => setShowInput(false)}
              />
            )}

            <div style={{marginTop: '1rem'}}>
              <NotesDisplay
                videoId={videoId}
                focusNoteId={initialReviewTarget?.videoId===videoId?initialReviewTarget.noteId:null}
                mapId={mapId}
                matchId={initialReviewTarget?.videoId===videoId&&initialReviewTarget?.noteMode==='video'?null:matchId}
                currentTime={currentTime}
                syncOffsetSec={syncOffsetSec}
                onSeek={() => {}}
                displayMode="list"
                isPlaying={true}
                autoScroll={false}
                onDisableAuto={() => {}}
                filters={filters}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-review-container">
      {/* ── BREADCRUMB / RETURN BUTTON BAR ── */}
      <div className="vpwn-breadcrumb">
        <span className="vpwn-breadcrumb-link" onClick={onBackToMaps}>
          Map Selection
        </span>
        <span className="vpwn-breadcrumb-sep">/</span>

        <span className="vpwn-breadcrumb-link" onClick={onBackToVideos}>
          {mapName}
        </span>
        <span className="vpwn-breadcrumb-sep">/</span>

        <span className="vpwn-breadcrumb-current">
          {(matchTitle || `Match ${matchId?.slice(0, 8)}`) +
            (videoData?.povName ? ` • ${videoData.povName}` : "")}
        </span>
      </div>

      

      {videoData && (
        <>
          {canAddNotes && (
            <FocusPoints
              confirmRemoval={confirmFocusRemoval}
              showOutcomes={showFocusOutcomes}
              mapId={mapId}
              videoId={videoId}
              matchId={matchId}
              focusPoints={focusPoints}
              teamId={teamId}
            />
          )}

          <ReviewPaneLayout>
            {/* A) Video side */}
            <div
              className="resizeable-video-wrapper"
              ref={videoContainerRef}
              style={{width:'100%'}}
            >
              {/* Fullscreen shell so we can render overlays/panels when fullscreen */}
              <div
                ref={fsShellRef}
                className={`fs-shell ${isSoftFullscreen ? 'fs-shell--soft' : ''} ${forceLandscapeSoftFs ? 'fs-shell--force-landscape' : ''}`}
              >
                <div
                  className="video-stage"
                  style={{ position: 'relative' }}
                  onMouseMove={markControlsActive}
                  onPointerMove={markControlsActive}
                  onTouchStart={markControlsActive}
                >
                  {isProcessingVideo ? (
                    <div className="video-processing-panel" aria-live="polite">
                      <div className="video-processing-panel__eyebrow">
                        Video Optimization In Progress
                      </div>
                      <h3 className="video-processing-panel__title">
                        Playback will unlock as soon as the processed file is ready
                      </h3>
                      <p className="video-processing-panel__message">
                        {processingView.explanation}
                      </p>
                      <p className="video-processing-panel__message">
                        {processingView.message}
                      </p>
                      <div
                        className="video-processing-progress"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={processingView.progressPercent}
                        aria-label="Video processing progress"
                      >
                        <div
                          className="video-processing-progress__fill"
                          style={{width: `${processingView.progressPercent}%`}}
                        />
                      </div>
                      <div className="video-processing-panel__meta">
                        <span>{processingView.progressPercent}% complete</span>
                        <span>
                          {processingView.etaLabel ?
                            `Estimated time remaining: ${processingView.etaLabel}` :
                            'Estimated time remaining will appear once processing starts.'}
                        </span>
                      </div>
                    </div>
                  ) : isFailedVideo ? (
                    <div className="video-missing-src">
                      {videoData?.processingMessage ||
                        'This video could not be prepared for playback. Please retry the upload or contact support.'}
                    </div>
                  ) : (sourceError || playbackBlockedMessage || (videoData && !signedSrc)) ? (
                    <div className="video-missing-src">
                      {sourceError || playbackBlockedMessage || (sourceLoading ? 'Loading video…' : 'Video is preparing or unavailable. Try again in a moment.')}
                      {sourceError && <button type="button" onClick={retryPlayback}>Retry playback</button>}
                    </div>
                  ) : null}

                  {mediaError && <div className="vpwn-playback-error" role="alert">
                    <p>{mediaError}</p><button type="button" onClick={retryPlayback}>Retry playback</button>
                  </div>}
                  {canRenderVideo && (
                    <>
                      <video
                        ref={videoRef}
                        preload={/\.webm(?:$|[?#])/i.test(videoData?.storagePath || videoData?.videoURL || '') ? 'auto' : 'metadata'}
                        crossOrigin="anonymous"
                        src={signedSrc || undefined}

                        playsInline
                        webkit-playsinline="true"
                        disablePictureInPicture

                        onLoadedMetadata={() => {
                          setViewedMedia(mediaIdentity);
                          applyPendingSeek();
                          syncDuration();
                        }}
                        onError={handleMediaError}
                        onLoadedData={() => {setIsBuffering(false); setMediaError('');}}
                        onWaiting={() => setIsBuffering(true)}
                        onStalled={() => setIsBuffering(true)}
                        onSeeking={() => setIsBuffering(true)}
                        onCanPlay={() => {
                          setIsBuffering(false);
                          setMediaError('');
                          applyPendingSeek();
                          syncDuration();
                        }}
                        onSeeked={() => setIsBuffering(false)}
                        onPlaying={() => {setIsBuffering(false); setMediaError('');}}
                        onDurationChange={syncDuration}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={handleEnded}
                        onClick={() => {
                          if (isCoarsePointer()) return;

                          if (suppressNextVideoClickRef.current) {
                            suppressNextVideoClickRef.current = false;
                            return;
                          }

                          if (singleClickTimerRef.current) return;

                          singleClickTimerRef.current = setTimeout(() => {
                            singleClickTimerRef.current = null;
                            const v = videoRef.current;
                            if (!v) return;
                            if (v.paused) void playVideo(v);
                            else v.pause();
                          }, SINGLE_CLICK_DELAY);
                        }}
                        onDoubleClick={(e) => {
                          e.preventDefault();

                          if (singleClickTimerRef.current) {
                            clearTimeout(singleClickTimerRef.current);
                            singleClickTimerRef.current = null;
                          }

                          suppressNextVideoClickRef.current = true;
                          requestFullscreenNow();
                        }}
                        className="video-player"
                      />

                      {isBuffering && (
                        <div className="vpwn-buffering-indicator" aria-live="polite">
                          <div className="vpwn-buffering-spinner"></div>
                        </div>
                      )}

                      <VideoControlsBar
                        videoRef={videoRef}
                        onPlay={() => void playVideo()}
                        onPause={() => videoRef.current?.pause()}
                        isFullscreen={isFullscreen}
                        onToggleFullscreen={requestFullscreenNow}
                        onAboutToToggleFullscreen={() => { suppressNextVideoClickRef.current = true; }}
                        currentTime={currentTime}
                        setCurrentTime={(t) => {
                          if (!videoRef.current) return;
                          videoRef.current.currentTime = t;
                        }}
                        onVolumeChange={(v) => {
                          if (!videoRef.current) return;
                          videoRef.current.muted = false;
                          videoRef.current.volume = v;
                        }}
                        onMuteToggle={() => {
                          if (!videoRef.current) return;
                          videoRef.current.muted = !videoRef.current.muted;
                        }}
                        onRateChange={(r) => {
                          if (!videoRef.current) return;
                          videoRef.current.playbackRate = r;
                        }}
                        duration={durationSec}
                        isPlaying={isPlaying}
                        volume={videoRef.current?.muted ? 0 : (videoRef.current?.volume ?? 1)}
                        playbackRate={videoRef.current?.playbackRate ?? 1}
                        showFsNotes={showFsNotes}
                        onToggleNotes={toggleFullscreenNotes}
                        isMuted={!!videoRef.current?.muted}
                        onCaptureScreenshot={handleCaptureScreenshot}
                        povOptions={(matchPovs || []).map(p => ({ id: p.id, title: getPovOptionLabel(p) }))}
                        activePovId={videoId}
                        onSelectPov={(nextId) => handleSwitchPovClick(nextId)}
                        uiHidden={!controlsVisible && isPlaying}
                        onUserActivity={markControlsActive}
                      />
                    </>
                  )}
                </div>

                {/* Fullscreen Notes panel appears to the right of the video */}
                {isFullscreen && showFsNotes && (
                  <>
                    <div
                      className="fs-notes-resizer"
                      onMouseDown={startFsNotesDrag}
                      aria-label="Resize notes panel"
                      title="Drag to resize notes"
                    />
                    <aside
                      className="fs-notes-panel"
                      aria-label="Notes panel (fullscreen)"
                      style={{
                        width: `${fsNotesWidthRem}rem`,
                        minWidth: `${fsNotesWidthRem}rem`,
                        maxWidth: `${fsNotesWidthRem}rem`,
                      }}
                    >
                      <div className="notes-panel-header">
                        <div className="notes-header-left">
                          {showInput ? (
                            <button className="hide-btn" onClick={() => setShowInput(false)}>
                              Hide Notes
                            </button>
                          ) : (
                            <button className="show-input-btn" onClick={() => setShowInput(true)}>
                              + Add Note
                            </button>
                          )}
                        </div>

                        <div className="notes-header-center">
                          <button
                            className="Rev-VidPlayNotes-mode-btn Finished-reviewing-btn"
                            onClick={handleFinishedReviewing}
                          >
                            Review Done
                          </button>
                        </div>

                        <div className="notes-header-right">
                          <button
                            className={
                              displayMode === 'list'
                                ? 'Rev-VidPlayNotes-mode-btn selected'
                                : 'Rev-VidPlayNotes-mode-btn'
                            }
                            onClick={() => onModeButtonClick('list')}
                          >
                            List
                          </button>

                          <button
                            className={
                              displayMode === 'scroller'
                                ? 'Rev-VidPlayNotes-mode-btn selected'
                                : 'Rev-VidPlayNotes-mode-btn'
                            }
                            onClick={() => onModeButtonClick('scroller')}
                          >
                            {displayMode === 'scroller' && !autoScroll ? 'Recenter' : 'Scroller'}
                          </button>

                          {(<div className="filter-wrapper" ref={filterWrapperRef}>
                            <button
                              className="Rev-VidPlayNotes-mode-btn review-filter-toggle" aria-label="Note filters" title="Note filters" aria-expanded={showFilterPanel}
                              onClick={() => setShowFilterPanel((v) => !v)}
                            ><SlidersHorizontal aria-hidden="true" size={18}/></button>

                            {showFilterPanel && (
                              <ReviewFilterPopover anchorRef={filterWrapperRef} onClose={() => setShowFilterPanel(false)}>
                                <div className="filter-panel-header">
                                  <span className="filter-title">Filters</span>
                                  <button
                                    className="Filter-Clear-All-btn"
                                    onClick={() => {
                                      setFilterMentions([]);
                                      setFilterTags([]);
                                      setFilterImportance([]);
                                      setHideReviewedByMe(false);
                                    }}
                                  >
                                    ✖ Clear All
                                  </button>
                                </div>

                                <div className="filter-group">
                                  <label>Filter by @Mention:</label>
                                  <div className="mention-checkboxes">
                                    {['@Players', ...mentionOptions].map((mention) => {
                                      const mentionKey = mention.toLowerCase().replace(/^@/, '');
                                      const isChecked = filterMentions.includes(mentionKey);
                                      return (
                                        <label key={mention} className="mention-option" style={mention === '@Players' ? {color: 'var(--color-role-player)'} : undefined}>
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setFilterMentions([...filterMentions, mentionKey]);
                                              } else {
                                                setFilterMentions(
                                                  filterMentions.filter((m) => m !== mentionKey)
                                                );
                                              }
                                            }}
                                          />
                                          {mention}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div className="filter-group">
                                  <label>Filter by Tags:</label>
                                  <div className="tag-checkboxes">
                                    {tagOptions.map((tag) => {
                                      const isChecked = filterTags.includes(tag);
                                      return (
                                        <label
                                          key={tag}
                                          className={`tag-option ${isChecked ? 'selected' : ''}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setFilterTags([...filterTags, tag]);
                                              } else {
                                                setFilterTags(filterTags.filter((t) => t !== tag));
                                              }
                                            }}
                                          />
                                          #{tag}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div className="filter-group">
                                  <label>Importance:</label>
                                  <div className="importance-row">
                                    {[1, 2, 3].map((n) => (
                                      <label key={n}>
                                        <input
                                          type="checkbox"
                                          checked={filterImportance.includes(n)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setFilterImportance([...filterImportance, n]);
                                            } else {
                                              setFilterImportance(
                                                filterImportance.filter((val) => val !== n)
                                              );
                                            }
                                          }}
                                        />
                                        {'!'.repeat(n)}
                                      </label>
                                    ))}
                                  </div>
                                </div>

                                <div className="filter-group">
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={hideReviewedByMe}
                                      onChange={(e) => setHideReviewedByMe(e.target.checked)}
                                    />
                                    Hide notes I’ve already reviewed
                                  </label>
                                </div>
                              </ReviewFilterPopover>
                            )}
                            </div>
                          )}
                        </div>
                      </div>

                      {showInput && canAddNotes && (
                        <div className="fs-note-input">
                          <NoteInputBox
                            mapId={mapId}
                            matchId={matchId}
                            matchTimeSec={currentMatchTimeSec}
                            onHide={() => setShowInput(false)}
                          />
                        </div>
                      )}

                      <div className="fs-notes-display">
                        <NotesDisplay
                          videoId={videoId}
                          focusNoteId={initialReviewTarget?.videoId===videoId?initialReviewTarget.noteId:null}
                          mapId={mapId}
                          matchId={initialReviewTarget?.videoId===videoId&&initialReviewTarget?.noteMode==='video'?null:matchId}
                          currentTime={currentTime}
                          syncOffsetSec={syncOffsetSec}
                          onSeek={(videoTimeSec) => {
                            if (!videoRef.current) return;
                            videoRef.current.currentTime = Math.max(0, Number(videoTimeSec || 0));
                            if (videoRef.current.paused) void playVideo();
                          }}
                          displayMode={displayMode}
                          isPlaying={isPlaying}
                          autoScroll={autoScroll}
                          onDisableAuto={handleDisableAutoScroll}
                          filters={filters}
                        />
                      </div>
                    </aside>
                  </>
                )}
              </div>
            </div>

            {screenshotEditorOpen && (
              <ScreenshotEditor
                image={capturedImage}
                onCancel={() => {
                  setScreenshotEditorOpen(false);
                  setCapturedImage(null);
                  if (videoRef.current) void playVideo();
                }}
                onSave={(finalImageDataURL) => {
                  setScreenshotEditorOpen(false);
                  window.dispatchEvent(
                    new CustomEvent('screenshotCaptured', {
                      detail: finalImageDataURL,
                    })
                  );
                }}
              />
            )}

            {/* B) Vertical splitter between video & notes */}


            {/* C) Notes panel */}
            {!isFullscreen && (
              <div className="notes-panel">
                <div className="notes-panel-header">
                  <div className="notes-header-left">
                    {showInput ? (
                      <button className="hide-btn" onClick={() => setShowInput(false)}>
                        Hide Notes
                      </button>
                    ) : (
                      <button className="show-input-btn" onClick={() => setShowInput(true)}>
                        + Add Note
                      </button>
                    )}
                  </div>

                  <div className="notes-header-center">
                    <button
                      className="Rev-VidPlayNotes-mode-btn Finished-reviewing-btn"
                      onClick={handleFinishedReviewing}
                    >
                      Finished Reviewing
                    </button>
                  </div>

                  <div className="notes-header-right">
                    <button
                      className={
                        displayMode === 'list'
                          ? 'Rev-VidPlayNotes-mode-btn selected'
                          : 'Rev-VidPlayNotes-mode-btn'
                      }
                      onClick={() => onModeButtonClick('list')}
                    >
                      List
                    </button>

                    <button
                      className={
                        displayMode === 'scroller'
                          ? 'Rev-VidPlayNotes-mode-btn selected'
                          : 'Rev-VidPlayNotes-mode-btn'
                      }
                      onClick={() => onModeButtonClick('scroller')}
                    >
                      {displayMode === 'scroller' && !autoScroll ? 'Recenter' : 'Scroller'}
                    </button>

                    {(<div className="filter-wrapper" ref={filterWrapperRef}>
                      <button
                        className="Rev-VidPlayNotes-mode-btn review-filter-toggle" aria-label="Note filters" title="Note filters" aria-expanded={showFilterPanel}
                        onClick={() => setShowFilterPanel((v) => !v)}
                      ><SlidersHorizontal aria-hidden="true" size={18}/></button>

                      {showFilterPanel && (
                        <ReviewFilterPopover anchorRef={filterWrapperRef} onClose={() => setShowFilterPanel(false)}>
                          <div className="filter-panel-header">
                            <span className="filter-title">Filters</span>
                            <button
                              className="Filter-Clear-All-btn"
                              onClick={() => {
                                setFilterMentions([]);
                                setFilterTags([]);
                                setFilterImportance([]);
                                setHideReviewedByMe(false);
                              }}
                            >
                              ✖ Clear All
                            </button>
                          </div>

                          <div className="filter-group">
                            <label>Filter by @Mention:</label>
                            <div className="mention-checkboxes">
                              {['@Players', ...mentionOptions].map((mention) => {
                                const mentionKey = mention.toLowerCase().replace(/^@/, '');
                                const isChecked = filterMentions.includes(mentionKey);
                                return (
                                  <label key={mention} className="mention-option" style={mention === '@Players' ? {color: 'var(--color-role-player)'} : undefined}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFilterMentions([...filterMentions, mentionKey]);
                                        } else {
                                          setFilterMentions(
                                            filterMentions.filter((m) => m !== mentionKey)
                                          );
                                        }
                                      }}
                                    />
                                    {mention}
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                          <div className="filter-group">
                            <label>Filter by Tags:</label>
                            <div className="tag-checkboxes">
                              {tagOptions.map((tag) => {
                                const isChecked = filterTags.includes(tag);
                                return (
                                  <label
                                    key={tag}
                                    className={`tag-option ${isChecked ? 'selected' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFilterTags([...filterTags, tag]);
                                        } else {
                                          setFilterTags(filterTags.filter((t) => t !== tag));
                                        }
                                      }}
                                    />
                                    #{tag}
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                          <div className="filter-group">
                            <label>Importance:</label>
                            <div className="importance-row">
                              {[1, 2, 3].map((n) => (
                                <label key={n}>
                                  <input
                                    type="checkbox"
                                    checked={filterImportance.includes(n)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFilterImportance([...filterImportance, n]);
                                      } else {
                                        setFilterImportance(
                                          filterImportance.filter((val) => val !== n)
                                        );
                                      }
                                    }}
                                  />
                                  {'!'.repeat(n)}
                                </label>
                              ))}
                            </div>
                          </div>

                          <div className="filter-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={hideReviewedByMe}
                                onChange={(e) => setHideReviewedByMe(e.target.checked)}
                              />
                              Hide notes I’ve already reviewed
                            </label>
                          </div>
                        </ReviewFilterPopover>
                      )}
                      </div>
                    )}
                  </div>
                </div>

                {showInput && canAddNotes && (
                  <NoteInputBox
                    mapId={mapId}
                    matchId={matchId}
                    matchTimeSec={currentMatchTimeSec}
                    onHide={() => setShowInput(false)}
                  />
                )}

                <div
                  className="notes-display-container"
                  ref={notesDisplayRef}
                  style={{ height: isNarrow ? '24rem' : `${notesDisplayHeight}px` }}
                >
                  <NotesDisplay
                    videoId={videoId}
                    focusNoteId={initialReviewTarget?.videoId===videoId?initialReviewTarget.noteId:null}
                    mapId={mapId}
                    matchId={initialReviewTarget?.videoId===videoId&&initialReviewTarget?.noteMode==='video'?null:matchId}
                    currentTime={currentTime}
                    syncOffsetSec={syncOffsetSec}
                    onSeek={(videoTimeSec) => {
                      if (!videoRef.current) return;
                      videoRef.current.currentTime = Math.max(0, Number(videoTimeSec || 0));
                      if (videoRef.current.paused) void playVideo();
                    }}
                    displayMode={displayMode}
                    isPlaying={isPlaying}
                    autoScroll={autoScroll}
                    onDisableAuto={handleDisableAutoScroll}
                    filters={filters}
                  />
                </div>

                <div className="notes-display-resizer" onMouseDown={startDisplayDrag} />
              </div>
            )}
          </ReviewPaneLayout>
        </>
      )}
    </div>
  );
}









// BREAK POINT OF FS IMPLMENTATION
